/**
 * Copyright © 2026 Adnuntius AS.
 */
import {
  Component,
  DestroyRef,
  DoCheck,
  Inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  computed,
  inject,
  signal
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NgForm} from '@angular/forms';
import {StateService} from '@uirouter/core';
import {finalize} from 'rxjs';
import * as _ from 'lodash';

import template from './site-analysis-page.component.html';
import {convertIabCategoryToString} from '../../components/util/iab-taxonomy';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {AdUnitBulkActionService} from '../ad-unit/ad-unit-bulk-action.service';
import {resetDimensionsBasedOnAdUnitOption} from '../ad-unit/ad-unit-commons';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import {SignalAdminResourcePageState} from '../common/controller/signal-admin-resource-page-state';
import {SignalAdminListColumn, SignalAdminResourceListStateFactory} from '../common/controller/signal-admin-resource-list-state';
import {ADVERTISING_SHARED} from '../advertising/advertising-shared';
import {AdUnitApiService, AdUnitModel} from '../ad-unit/ad-unit-api.service';
import {AdnModalLoadingService} from '../common/util/adn-modal-loading.service';
import {bulkApiErrorsFromResponse} from '../common/controller/bulk-action-errors';
import {Uuid} from '../../components/util/uuid';
import {SiteApiService, SiteArticle} from './site-api.service';

type AnalysisTab = 'adUnits' | 'charts' | 'adUnitsCharts' | 'profiles' | 'reports' | 'traffic' | 'location' | 'availability' | 'explore';

@Component({
  standalone: true,
  imports: [...ADVERTISING_SHARED],
  selector: 'adn-site-analysis-page',
  template
})
export class SiteAnalysisPageComponent implements DoCheck, OnChanges, OnInit {
  @Input() model: any;
  @Input() state: SignalAdminResourcePageState<any>;
  @Input() isNew: boolean;

  effectiveIsNew = false;
  notesSaveTarget: any;
  activeTab: AnalysisTab = 'adUnits';

  readonly purposeMatrix: Record<number, string> = {
    1: 'Store and/or access information on a device',
    2: 'Select basic ads',
    3: 'Create a personalized ads profile',
    4: 'Select personalized ads',
    7: 'Measure ad performance'
  };
  private readonly relevantPurposes = [1, 2, 4];

  readonly auFields: SignalAdminListColumn[];
  readonly adUnits = inject(SignalAdminResourceListStateFactory).create<AdUnitModel>(
    inject(AdUnitApiService),
    {listId: 'fullAdUnitsforSite', additionalParams: {}, baseTable: 'adUnit', syncPageToUrl: false},
    inject(DestroyRef)
  );
  readonly adUnitSaving = signal(false);
  readonly adUnitSaveFailed = signal(false);
  readonly selectedAdUnitChoice = signal<AdUnitModel | undefined>(undefined);
  readonly selAdUnit = computed(() => {
    const items = this.adUnits.items();
    const selectedId = this.selectedAdUnitChoice()?.id;
    return items.find((item) => item.id === selectedId) || items[0];
  });
  adUnit: AdUnitModel;
  showAdUnitForm = false;

  showAdCount = false;
  showNotes = false;
  adminUserName = '';
  canSeeReports = false;
  isPureMarketplace = false;
  showConsent = false;
  showSemantic = false;
  showContentProfile = -1;

  theUrl = '';
  private readonly queryRunningState = signal(false);
  private readonly adCountRunningState = signal(false);
  private readonly adCountsRunningState = signal(false);
  private readonly recentArticlesRunningState = signal(false);
  private readonly viewedArticlesRunningState = signal(false);
  private readonly triggerDoneState = signal(false);
  private readonly isRunningState = signal(false);

  get queryRunning(): boolean { return this.queryRunningState(); }
  set queryRunning(value: boolean) { this.queryRunningState.set(value); }
  get adCountRunning(): boolean { return this.adCountRunningState(); }
  set adCountRunning(value: boolean) { this.adCountRunningState.set(value); }
  get adCountsRunning(): boolean { return this.adCountsRunningState(); }
  set adCountsRunning(value: boolean) { this.adCountsRunningState.set(value); }
  get recentArticlesRunning(): boolean { return this.recentArticlesRunningState(); }
  set recentArticlesRunning(value: boolean) { this.recentArticlesRunningState.set(value); }
  get viewedArticlesRunning(): boolean { return this.viewedArticlesRunningState(); }
  set viewedArticlesRunning(value: boolean) { this.viewedArticlesRunningState.set(value); }
  get triggerDone(): boolean { return this.triggerDoneState(); }
  set triggerDone(value: boolean) { this.triggerDoneState.set(value); }
  get isRunning(): boolean { return this.isRunningState(); }
  set isRunning(value: boolean) { this.isRunningState.set(value); }
  info: any;
  infoError = false;
  triggerMessage: number | null = null;

  adCount: any;
  adCounts: any;
  recentArticles: SiteArticle[];
  viewedArticles: SiteArticle[];
  noConsentResults = false;
  consent: any;
  siteHost = '';

  showingChart = '';
  showingTraffic = false;
  showingLocation = false;
  showingReach = false;
  showingExplorer = false;
  visibleReports = false;

  readonly adUnitItemStateName = 'app.inv.ad-units.ad-unit';
  private lastLoadedModelId: string | undefined;
  private readonly adUnitApi = inject(AdUnitApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly modalLoading = inject(AdnModalLoadingService);

  constructor(
    private readonly $state: StateService,
    @Inject(LocalUserPermissions) LocalUserPermissions: any,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    @Inject(LocalUserProfile) localUserProfile: any,
    private readonly siteApi: SiteApiService,
    private readonly adUnitBulkActionService: AdUnitBulkActionService,
    private readonly localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()
  ) {
    const isSpecialUser = localUserProfile.get('username') === 'adnuntius@adnuntius.com'
      || localUserProfile.get('username') === 'broker1@adnuntius.com';
    if (isSpecialUser) {
      this.showAdCount = true;
      this.showNotes = true;
      this.adminUserName = localUserProfile.get('username');
    }
    this.canSeeReports = LocalUserPermissions.getAnyVisibility().RUN_REPORTS === true
      || LocalUserPermissions.getAnyVisibility().AX_PUBLISHER === true;
    this.isPureMarketplace = LocalNetworkProfile.isPureMarketplacePlatform();
    this.showConsent = LocalNetworkProfile.isPureMarketplacePlatform();
    this.showSemantic = LocalNetworkProfile.isSemanticPlatform();
    this.auFields = [
      {param: 'name'},
      {param: '', label: 'dimensions', sortable: false},
      {param: 'requestedInLastDay', label: 'status'},
      {param: '', label: 'tagId', sortable: false}
    ];
    if (this.LocalNetworkProfile.isPureMarketplacePlatform() && isSpecialUser) {
      this.auFields.push({label: 'isFullPage', param: 'fullPage'});
    }
  }

  get displayItems(): AdUnitModel[] {
    return this.adUnits.items();
  }

  get adUnitsEmpty(): boolean {
    return this.displayItems.length === 0;
  }

  get adUnitsCount(): number {
    return this.displayItems.length;
  }

  get showNewMessage(): boolean {
    return !!this.effectiveIsNew;
  }

  ngOnInit(): void {
    this.effectiveIsNew = !!this.isNew;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isNew) {
      this.effectiveIsNew = !!this.isNew;
    }
    if (changes.model) {
      this.notesSaveTarget = this.state ? this.state.notesSaveTarget() : this.model;
      this.updateSiteHost();
      this.canDoContentProfiles();
    }
    if (changes.state) {
      this.adUnits.readOnly.set(this.state?.readOnly() || false);
    }
    this.refreshAdUnitsIfNeeded();
  }

  ngDoCheck(): void {
    const modelId = this.model?.id;
    if (modelId && modelId !== this.lastLoadedModelId && !this.effectiveIsNew) {
      this.refreshAdUnitsIfNeeded();
    }
  }

  selectTab(tab: AnalysisTab): void {
    this.activeTab = tab;
    if (tab === 'charts') {
      this.showingChart = 'SITE';
    } else if (tab === 'adUnitsCharts') {
      this.showingChart = 'AD_UNIT';
    } else if (tab === 'profiles') {
      this.queryConsent();
      this.canDoContentProfiles();
    } else if (tab === 'reports') {
      this.visibleReports = true;
    } else if (tab === 'traffic') {
      this.showingTraffic = true;
    } else if (tab === 'location') {
      this.showingLocation = true;
    } else if (tab === 'availability') {
      this.showingReach = true;
    } else if (tab === 'explore') {
      this.showingExplorer = true;
    }
  }

  hasColumn(param: string): boolean {
    return this.auFields.some((column) => column.param === param);
  }

  loadAdUnitForm(): void {
    if (this.adUnits.readOnly()) {
      return;
    }
    this.adUnit = {
      id: Uuid.generate(),
      site: this.model,
      objectState: 'ACTIVE',
      validationWarnings: [],
      urlAsCategory: true,
      pageSize: 1,
      targetingControl: 'NONE',
      floorPrice: {},
      dimensions: [[]],
      renderOption: null,
      adUnitOption: 'RANGE'
    };
    this.adUnitSaveFailed.set(false);
    this.showAdUnitForm = true;
  }

  saveAdUnit(editForm: NgForm): void {
    if (editForm.invalid || this.adUnitSaving() || this.adUnits.readOnly()) {
      return;
    }
    this.onAdUnitBeforeSubmit();
    this.adUnitSaving.set(true);
    this.adUnitSaveFailed.set(false);
    this.adUnitApi.save(_.cloneDeep(this.adUnit)).pipe(
      finalize(() => {
        this.adUnitSaving.set(false);
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: () => {
        this.showAdUnitForm = false;
        this.adUnits.load();
      },
      error: () => this.adUnitSaveFailed.set(true)
    });
  }

  openBulkActions(): void {
    const adapter: any = {
      bulkSelection: this.adUnits.selected(),
      miscBulkAction: (modal: any, modalController: any, items: Partial<AdUnitModel>[]) => {
        if (!items.length) {
          modal.close();
          return;
        }
        this.modalLoading.applyModalLoading();
        this.adUnits.saveMiscBulkAction(items).pipe(
          finalize(() => this.modalLoading.removeModalLoading()),
          takeUntilDestroyed(this.destroyRef)
        ).subscribe({
          next: () => modal.close({items}),
          error: (response) => {
            modalController.form = modalController.form || {};
            modalController.form.apiErrors = bulkApiErrorsFromResponse(response);
            modalController.form.bulkError = true;
          }
        });
      }
    };
    this.adUnitBulkActionService.createBulkAction(adapter)();
  }

  openSelectedAsAdTags(): void {
    const tags = _.map(this.adUnits.selected(), (adUnit) => ({
      auId: adUnit.tagId,
      auW: adUnit.width || adUnit.dimensions?.[0]?.[0] || '',
      auH: adUnit.height || adUnit.dimensions?.[0]?.[1] || '',
      auName: adUnit.name
    }));
    this.$state.go('app.ad-tag', {design: JSON.stringify(tags)});
  }

  removeDimensions(index: number): void {
    _.pullAt(this.adUnit.dimensions, index);
  }

  addDimensions(): void {
    this.adUnit.dimensions = this.adUnit.dimensions || [];
    this.adUnit.dimensions.push([]);
  }

  onAdUnitBeforeSubmit(): void {
    if (this.adUnit.adUnitOption === 'RANGE') {
      this.adUnit.dimensions = null;
    } else {
      this.adUnit.minWidth = null;
      this.adUnit.minHeight = null;
      this.adUnit.height = null;
      this.adUnit.width = null;
      resetDimensionsBasedOnAdUnitOption(this.adUnit);
    }
  }

  queryUrl(): void {
    this.queryRunning = true;
    this.info = {};
    this.siteApi.getContentProfile(this.model.id, this.theUrl).pipe(
      finalize(() => {
        this.queryRunning = false;
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((profileInfo) => {
        this.infoError = false;
        if (_.get(profileInfo, ['parameters', 'errorCode']) === 'ECFSR001') {
          this.infoError = true;
        } else if (_.get(profileInfo, ['content'])) {
          profileInfo.contentTypes = [];
          _.forEach(profileInfo.content, (val: any, key: string) => {
            if (key === 'categories') {
              profileInfo.categoriesOutput = _.map(val, (cat: any) => convertIabCategoryToString(cat)).join(', ');
            } else {
              profileInfo[key + 'Output'] = val.join(', ');
            }
            profileInfo.contentTypes.push(key);
          });
        }
        this.info = profileInfo;
    });
  }

  getAdCount(): void {
    this.adCountRunning = true;
    this.adCount = {};
    this.siteApi.getAdCount(this.model.id).pipe(
      finalize(() => {
        this.adCountRunning = false;
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((count) => {
        this.adCount = count;
    });
  }

  getAdCounts(): void {
    this.adCountsRunning = true;
    this.adCounts = {};
    this.siteApi.getAdCounts(this.model.id).pipe(
      finalize(() => {
        this.adCountsRunning = false;
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((counts) => {
        this.adCounts = counts;
    });
  }

  canDoContentProfiles(): void {
    if (!this.model?.siteUrl) {
      this.showContentProfile = 0;
      return;
    }
    this.showContentProfile = this.localNetworkInfo.getInfo('contextServiceConfigurationCount');
  }

  getRecentArticles(): void {
    this.recentArticlesRunning = true;
    this.siteApi.listRecentArticles(this.model.id, this.getDomainFromUrl(this.model.siteUrl)).pipe(
      finalize(() => {
        this.recentArticlesRunning = false;
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (page) => {
        this.recentArticles = this.prepareArticles(page.results);
      },
      error: () => undefined
    });
  }

  getViewedArticles(): void {
    this.viewedArticlesRunning = true;
    this.siteApi.listViewedArticles(this.model.id, this.getDomainFromUrl(this.model.siteUrl)).pipe(
      finalize(() => {
        this.viewedArticlesRunning = false;
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (page) => {
        this.viewedArticles = this.prepareArticles(page.results);
      },
      error: () => undefined
    });
  }

  triggerProfileRefresh(): void {
    this.triggerDone = true;
    this.siteApi.refreshContentProfile(this.theUrl).pipe(
      finalize(() => {
        this.triggerDone = false;
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: () => {
        this.triggerMessage = 1;
      },
      error: () => {
        this.triggerMessage = 0;
      }
    });
  }

  queryConsent(): void {
    if (!this.model?.siteUrl) {
      return;
    }
    this.noConsentResults = false;
    if (!this.siteHost) {
      this.noConsentResults = true;
    }
    this.isRunning = true;
    this.siteApi.getConsentStatistics(this.model.id).pipe(
      finalize(() => {
        this.isRunning = false;
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (data) => {
        this.consent = data;
        if (_.get(this.consent, ['purpose'])) {
          this.consent.purpose = _.pick(this.consent.purpose, this.relevantPurposes);
        }
        _.forEach(_.get(this.consent, ['purpose']), (p: any) => {
          p.maxPercent = Math.max(p.consentedPercent, p.legitimateInterestPercent);
          p.totalUsersPercent = this.consent.percentWithConsentString * p.maxPercent / 100;
          p.htmlClass = p.totalUsersPercent > 94.9 ? 'text-success' : p.totalUsersPercent > 74.9 ? 'text-warning' : 'text-danger';
          p.failures = [];
          if (this.consent.percentWithConsentString < 99.95) {
            p.failures.push({percentage: (100 - this.consent.percentWithConsentString), reason: 'without a consent string'});
          }
          if (p.consentedPercent > p.legitimateInterestPercent && p.consentedPercent < 99.95) {
            p.failures.push({percentage: (100 - p.consentedPercent), reason: 'did not consent'});
          }
          if (p.legitimateInterestPercent > p.consentedPercent && p.legitimateInterestPercent < 99.95) {
            p.failures.push({percentage: (100 - p.legitimateInterestPercent), reason: 'opted out of legitimate interest'});
          }
        });
        if (_.get(this.consent, ['purpose'])) {
          _.forEach(this.relevantPurposes, (purposeKey: number) => {
            if (!this.consent.purpose[purposeKey]) {
              this.consent.purpose[purposeKey] = {
                totalUsersPercent: 0,
                htmlClass: 'text-danger',
                failures: [{percentage: 100, reason: 'without a value at all'}]
              };
            }
          });
        }
      },
      error: () => {
        this.noConsentResults = true;
      }
    });
  }

  copyTagId(tagId: string): void {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(tagId);
    }
  }

  purposeKeys(): number[] {
    if (!this.consent?.purpose) {
      return [];
    }
    return _.map(_.keys(this.consent.purpose), (key) => parseInt(key, 10));
  }

  private refreshAdUnitsIfNeeded(force = false): void {
    const modelId = this.effectiveIsNew ? undefined : this.model?.id;
    if (!force && modelId === this.lastLoadedModelId) {
      return;
    }
    this.lastLoadedModelId = modelId;
    if (!modelId) {
      this.adUnits.items.set([]);
      this.adUnits.totalCount.set(0);
      this.adUnits.selected.set({});
      this.adUnits.loading.set(false);
      return;
    }
    if (force && this.adUnits.additionalParams().site === modelId) {
      this.adUnits.load();
    } else {
      this.adUnits.setAdditionalParams({site: modelId});
    }
  }

  private updateSiteHost(): void {
    this.siteHost = '';
    if (!this.model?.siteUrl) {
      return;
    }
    try {
      this.siteHost = new URL(this.model.siteUrl).hostname;
    } catch (e) {
      // ignore invalid site URL
    }
  }

  private getDomainFromUrl(url: string): string {
    if (!url || url.indexOf('http') !== 0) {
      return '';
    }
    return new URL(url).hostname;
  }

  private prepareArticles(articles: SiteArticle[] = []): SiteArticle[] {
    return articles.map((article) => ({
      ...article,
      keywordsRead: (article.keywords || []).join(', ')
    }));
  }
}
